import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiFileText,
  FiMail,
  FiSearch,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import {
  APPLICATION_STATUSES,
  fetchAllApplications,
  fetchAllJobPosts,
  updateApplicationStatus,
} from "../../../services/jobApplicationsService";
import "./Applications.scss";

const AllApplications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterJobId, setFilterJobId] = useState("all");
  const [selectedApplication, setSelectedApplication] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobRows, appRows] = await Promise.all([
        fetchAllJobPosts(),
        fetchAllApplications({
          status: filterStatus,
          searchTerm,
          jobId: filterJobId,
        }),
      ]);
      setJobs(jobRows);
      setApplications(appRows);
    } catch (error) {
      console.error("Error fetching consolidated applications:", error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [filterJobId, filterStatus, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
      case "hired":
        return "success";
      case "rejected":
        return "error";
      case "shortlisted":
        return "info";
      case "interview":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
      case "hired":
        return <FiCheckCircle />;
      case "rejected":
        return <FiXCircle />;
      case "shortlisted":
      case "interview":
        return <FiClock />;
      default:
        return <FiFileText />;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const stats = useMemo(
    () => ({
      total: applications.length,
      shortlisted: applications.filter((app) => app.status === "shortlisted").length,
      pending: applications.filter(
        (app) => !app.status || app.status === "new" || app.status === "pending"
      ).length,
      interview: applications.filter((app) => app.status === "interview").length,
    }),
    [applications]
  );

  const handleStatusUpdate = async (application, status) => {
    try {
      await updateApplicationStatus({
        jobId: application.sourceJobId,
        applicationId: application.id,
        status,
      });
      if (selectedApplication?.id === application.id) {
        setSelectedApplication((prev) => (prev ? { ...prev, status } : prev));
      }
      loadData();
    } catch (error) {
      console.error("Failed to update application status:", error);
      alert("Failed to update status");
    }
  };

  return (
    <div className="applications-management">
      <div className="page-header">
        <div className="header-content">
          <h1>All Applications</h1>
          <p>Consolidated view across all job posts.</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total Applications</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>{stats.shortlisted}</h3>
            <p>Shortlisted</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>{stats.pending}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>{stats.interview}</h3>
            <p>Interview</p>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by candidate, email, phone, job..."
          />
        </div>
        <div className="filter-controls">
          <select
            value={filterJobId}
            onChange={(e) => setFilterJobId(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Jobs</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title || job.id}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            {APPLICATION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="applications-table-container">
        <table className="applications-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Job</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Applied</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>Loading applications...</td>
              </tr>
            ) : applications.length === 0 ? (
              <tr>
                <td colSpan={6}>No applications found.</td>
              </tr>
            ) : (
              applications.map((application) => (
                <tr key={`${application.sourceJobId}-${application.id}`}>
                  <td>
                    {`${application.firstName || ""} ${application.lastName || ""}`.trim() ||
                      "N/A"}
                  </td>
                  <td>{application.sourceJobTitle || "N/A"}</td>
                  <td>
                    <div className="contact-cell">
                      {application.email && (
                        <div className="contact-item">
                          <FiMail />
                          <a href={`mailto:${application.email}`}>{application.email}</a>
                        </div>
                      )}
                      {application.phone && (
                        <div className="contact-item">
                          <FiUser />
                          <span>{application.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusColor(application.status || "new")}`}>
                      {getStatusIcon(application.status || "new")}
                      {(application.status || "new").charAt(0).toUpperCase() +
                        (application.status || "new").slice(1)}
                    </span>
                  </td>
                  <td>{formatDate(application.createdAt || application.appliedAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon view"
                        title="View"
                        type="button"
                        onClick={() => setSelectedApplication(application)}
                      >
                        <FiEye />
                      </button>
                      {application.resumeUrl && (
                        <a
                          href={application.resumeUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-icon download"
                          title="Resume"
                        >
                          <FiDownload />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedApplication && (
        <div className="modal-overlay" onClick={() => setSelectedApplication(null)}>
          <div className="modal-content application-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Application Details</h2>
              <button className="close-btn" onClick={() => setSelectedApplication(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="application-details">
                <div className="detail-section">
                  <h3>Candidate</h3>
                  <p>
                    {`${selectedApplication.firstName || ""} ${
                      selectedApplication.lastName || ""
                    }`.trim()}
                  </p>
                  <p>
                    <strong>Job:</strong> {selectedApplication.sourceJobTitle || "N/A"}
                  </p>
                </div>
                <div className="detail-section">
                  <h3>Status</h3>
                  <div className="status-buttons">
                    {APPLICATION_STATUSES.map((status) => (
                      <button
                        key={status}
                        className="btn-status interview"
                        disabled={selectedApplication.status === status}
                        onClick={() => handleStatusUpdate(selectedApplication, status)}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllApplications;
